/**
 * useMinWidth.ts
 *
 * For BEHAVIOUR switches only — e.g. a bell that opens a drawer on desktop
 * instead of navigating to a full screen on phone. Never reach for this to
 * change layout: layout is CSS (`md:`/`lg:`/`xl:`), so an iPad in landscape
 * and a laptop at the same width render the same page without a JS branch
 * that could disagree with the CSS breakpoint it's meant to mirror.
 */

import { useSyncExternalStore } from 'react';

const QUERIES = {
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)',
} as const;

// One MediaQueryList per query, created lazily on first use and reused by
// every caller/render after that — `getSnapshot` runs on every render (and
// more, since useSyncExternalStore calls it to check for tearing), so
// calling `matchMedia` fresh in there allocated a new MediaQueryList
// constantly instead of the cheap `.matches` read it's meant to be.
const mediaQueryLists = new Map<string, MediaQueryList>();

function getMediaQueryList(query: string): MediaQueryList {
  let mql = mediaQueryLists.get(query);
  if (!mql) {
    mql = window.matchMedia(query);
    mediaQueryLists.set(query, mql);
  }
  return mql;
}

export function useMinWidth(tier: 'md' | 'lg' | 'xl'): boolean {
  const query = QUERIES[tier];

  return useSyncExternalStore(
    (onStoreChange) => {
      const mql = getMediaQueryList(query);
      mql.addEventListener('change', onStoreChange);
      return () => mql.removeEventListener('change', onStoreChange);
    },
    () => getMediaQueryList(query).matches,
    // Server snapshot: false — there is no server-rendered viewport, so the
    // conservative (phone) answer avoids a hydration mismatch.
    () => false
  );
}
